import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAutomationGrossingShelfSampleTypeAndShelfModal
    from '@/modules/configurationModule/detail/automation/grossing-shelf/sample-type-and-shelf/components/modal/configuration-automation-grossing-shelf-sample-type-and-shelf-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    ConfigurationAutomationGrossingShelfSampleTypeAndShelfGridState,
    initialConfigurationAutomationGrossingShelfSampleTypeAndShelfGridState
} from '@/modules/configurationModule/detail/automation/grossing-shelf/sample-type-and-shelf/components/grid/configuration-automation-grossing-shelf-sample-type-and-shelf-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ConfigurationShelfSampleTypeAssignmentCreateRequest,
    ConfigurationShelfSampleTypeAssignmentPatchInBatchRequest,
    ConfigurationShelfSampleTypeAssignmentResponse
} from '@/modules/configurationModule/detail/automation/grossing-shelf/sample-type-and-shelf/services/configuration-shelf-sample-type-assignment/configuration-shelf-sample-type-assignment.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAutomationGrossingShelfSampleTypeAndShelfModal
    }
})
export default class ConfigurationAutomationGrossingShelfSampleTypeAndShelfGrid extends BaseCrudServerSideComponent<ConfigurationShelfSampleTypeAssignmentResponse> implements BaseCrud {
    @Ref() readonly sampleTypeAndShelfModalWrapper!: typeof ModalWrapper

    state: ConfigurationAutomationGrossingShelfSampleTypeAndShelfGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationGrossingShelfSampleTypeAndShelfGridState()
    }

    /// SERVICES CALLS ///

    private async _createShelfSampleTypeAssignment(createRequest: ConfigurationShelfSampleTypeAssignmentCreateRequest): Promise<ConfigurationShelfSampleTypeAssignmentResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _patchShelfSampleTypeAssignments(batchRequests: ConfigurationShelfSampleTypeAssignmentPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(this.state.laboratoryId, batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    private async _deleteShelfSampleTypeAssignments(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: ConfigurationShelfSampleTypeAssignmentCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createShelfSampleTypeAssignment(createRequest)) {
                this.reloadData()
            }
        }
    }

    async patchOperation(batchRequest: ConfigurationShelfSampleTypeAssignmentPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchShelfSampleTypeAssignments([batchRequest])) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteShelfSampleTypeAssignments(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(shelfSampleTypeAssignment: ConfigurationShelfSampleTypeAssignmentResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (shelfSampleTypeAssignment) {
            this.showModal(this.$tc('configuration.actions.update'), shelfSampleTypeAssignment, this.sampleTypeAndShelfModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(shelfSampleTypeAssignment => shelfSampleTypeAssignment.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.sampleTypeAndShelfModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
